import { ChangeDetectorRef, Component, DestroyRef, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { REGISTER } from '@core/constants/RoutingsPath';
import { TokenStorageService } from '@core/services/token-storage.service';
import { firstValueFrom, Observable, Subject } from 'rxjs';
import { LoadingService } from '@core/services/loading.service';
import { Store } from '@ngrx/store';
import { isNewClient, loggedIn, selectUser } from '@core/modules/user/features/user.slice';
import { filter, finalize, first, map, withLatestFrom } from 'rxjs/operators';
import { User } from '@core/modules/user/models';
import { APP_CONFIG, AppConfig } from 'app/app.config';
import { AuthService } from '@core/modules/authentication/services/auth.service';
import { UserRole } from '@core/models';
import { GlobalInformationModalService } from '@shared/components/global-information-modal/global-information-modal.service';
import { CheckPolicyService } from '@modules/private/admin/popups-manager/pages/policy-popup/services/check-policy.service';
import { LoginError } from './login-error.type';
import { RETURN_BACK_AFTER_LOGIN_QUERY_PARAM } from '@core/constants/app-query-params';
import { RedirectAfterLoginService } from '@core/services/redirect-after-login.service';

const DoAfterLoginAction = {
  REDIRECT_TO_URL: 'REDIRECT_TO_URL',
  SHOW_POLICY: 'SHOW_POLICY',
  NAVIGATE_BY_USER_ROLE: 'NAVIGATE_BY_USER_ROLE'
} as const;

type DoAfterLoginAction = (typeof DoAfterLoginAction)[keyof typeof DoAfterLoginAction];

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit {
  registerPath = `/${REGISTER}`;
  loginError$: Observable<LoginError>;

  verifyingEmail = {
    isShown: false,
    email: ''
  };

  private loginErrorSubject: Subject<LoginError> = new Subject<LoginError>();

  get isShownLoginForm(): boolean {
    return !this.verifyingEmail.isShown;
  }

  get redirectUrl(): string | null {
    return this.redirectAfterLoginService.pending(
      this.activatedRoute.snapshot.queryParams[RETURN_BACK_AFTER_LOGIN_QUERY_PARAM]
    );
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private readonly activatedRoute: ActivatedRoute,
    private destroyRef: DestroyRef,
    private tokenStorageService: TokenStorageService,
    private loaderService: LoadingService,
    private store: Store,
    private changeDetectorRef: ChangeDetectorRef,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
    private globalInformationModalService: GlobalInformationModalService,
    private checkPolicyService: CheckPolicyService,
    private redirectAfterLoginService: RedirectAfterLoginService
  ) {
    this.loginError$ = this.loginErrorSubject.asObservable();
    this.isLoggedIn() && this.defaultNavigation();
  }

  ngOnInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  onSubmit(formValues: any) {
    this.loaderService.show();
    const { email, password } = formValues;
    this.authService
      .login(email, password)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.loaderService.hide())
      )
      .subscribe((response) => {
        const errorMsg = response.message;
        if (errorMsg) {
          this.loginErrorSubject.next({
            message: errorMsg,
            type: this.isNotActivateUser(errorMsg) ? 'NOT_ACTIVATE' : 'INCORRECT'
          });
        } else {
          this.tokenStorageService.saveToken(response.token);
          this.store.dispatch(loggedIn(this.getUserEmail()));

          this.defaultNavigation();
        }
      });
  }

  onCompleteVerification(email: string): void {
    this.verifyingEmail = {
      isShown: true,
      email
    };
    // NOTE:
    // activation link will send into verifying-email component after init email
  }

  private defaultNavigation(): void {
    this.loaderService.show();
    this.store
      .select(selectUser)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((user) => !!user),
        first(),
        map((user) => user as User),
        withLatestFrom(this.store.select(isNewClient))
      )
      .subscribe(async ([user, isNewClient]) => {
        this.loaderService.hide();

        const redirectUrl = this.redirectUrl;

        const navigateAfterLoginByUserRole = async () => {
          // A new client is sent to the captive portal first: the pending url is
          // kept so the onboarding can hand it over once the profile is done.
          !isNewClient && this.redirectAfterLoginService.clear();

          await this.router.navigate(
            this.appConfig.LOGIN_INITIAL_NAVIGATION.get(
              isNewClient ? UserRole.NEW_CLIENT : user.roleId
            )!
          );
        };

        const actions = {
          [DoAfterLoginAction.REDIRECT_TO_URL]: async () => {
            this.redirectAfterLoginService.clear();

            await this.router.navigateByUrl(redirectUrl!);
          },

          [DoAfterLoginAction.SHOW_POLICY]: async () => {
            const response = await firstValueFrom(
              this.checkPolicyService.checkHasNewPolicy(user.id)
            );

            if (response?.isAccepted === false) {
              this.globalInformationModalService.open(
                response.translation,
                navigateAfterLoginByUserRole.bind(this)
              );
            } else {
              return navigateAfterLoginByUserRole();
            }
          },

          [DoAfterLoginAction.NAVIGATE_BY_USER_ROLE]: async () => navigateAfterLoginByUserRole()
        };

        const actionName: DoAfterLoginAction = (() => {
          if (redirectUrl && !isNewClient) {
            return DoAfterLoginAction.REDIRECT_TO_URL;
          }

          if ([UserRole.CLIENT].includes(user.roleId)) {
            return DoAfterLoginAction.SHOW_POLICY;
          }

          return DoAfterLoginAction.NAVIGATE_BY_USER_ROLE;
        })();

        await actions[actionName]();
      });
  }

  private isLoggedIn(): boolean {
    return !!this.tokenStorageService.getToken();
  }

  private getUserEmail(): string {
    return this.tokenStorageService.getToken()?.user.email;
  }

  private isNotActivateUser(error: string): boolean {
    return error === 'User is not activate';
  }
}
